import * as React from 'react';

import { LucideChevronDown, LucideLoader } from 'lucide-react';
import { Select as AriaSelect, ValidationResult } from 'react-aria-components';
import useResizeObserver from 'use-resize-observer';

import { cn, scrollIntoView } from '../../..';
import { useTranslations } from '../../../i18n/useTranslations';
import { FieldError, Label, fieldGroupVariants } from '../Field';
import { Popover } from '../overlays/Popover';

import { useSelectHelpers } from './useSelectHelpers';

export interface SelectProps<K extends string | number = string> {
  onChange?: (key: K) => void;
  value?: K | undefined | null;
  defaultValue?: K;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  options?: {
    label: React.ReactNode;
    selectedLabel?: string;
    searchable?: string;
    key: K;
    isDisabled?: boolean;
  }[];
  fullHeight?: boolean;
  isLoading?: boolean;
  showArrow?: boolean;
  className?: string;
  preFocused?: boolean;
  destructive?: boolean;
  onKeyDown?: React.KeyboardEventHandler;
  onFocus?: React.FocusEventHandler;
  selectionBoxClassName?: string;
  withEmptyOption?: boolean;
  emptyOptionLabel?: string;
  ref?: React.RefObject<HTMLDivElement>;
  autoSelectFirstOption?: boolean;
  autoFocus?: boolean;
}

export const Select = <K extends string>({
  label,
  autoSelectFirstOption,
  errorMessage,
  options,
  fullHeight,
  isDisabled,
  isRequired,
  showArrow = true,
  onChange: onSelectionChange,
  placeholder = 'Select',
  onKeyDown,
  onFocus,
  selectionBoxClassName,
  withEmptyOption,
  emptyOptionLabel,
  ref,
  isInvalid,
  autoFocus,
  ...props
}: SelectProps<K>) => {
  const t = useTranslations();

  const [query, setQuery] = React.useState('');
  const { ref: inputContainerRef, width: inputContainerWidth } =
    useResizeObserver();
  const optionsContainerRef = React.useRef<HTMLDivElement>(null);
  const { extractTextContentFromReactNode } = useSelectHelpers();

  // Use selectedKey if provided (controlled mode), otherwise use internal state (uncontrolled mode)
  const effectiveSelectedKey = props.value;

  const normalizedOptions = React.useMemo(
    () =>
      options?.map((it) => {
        const normalizedLabelText = extractTextContentFromReactNode(it.label)
          .toLowerCase()
          .replace(/-/g, '');

        return {
          ...it,
          searchable: [it.searchable, normalizedLabelText]
            .filter(Boolean)
            .join(' '),
        };
      }),
    [extractTextContentFromReactNode, options],
  );

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = query.toLowerCase().replace(/-/g, '');
    const queryTerms = normalizedQuery.split(' ');

    const filtered =
      normalizedOptions
        ?.filter((it) =>
          queryTerms.every((term) => it.searchable.includes(term)),
        )
        .map((it) => ({
          id: it.key,
          label: it.label,
          isDisabled: it.isDisabled,
        })) ?? [];

    if (withEmptyOption && !query) {
      return [
        { id: undefined, label: emptyOptionLabel ?? 'None', isDisabled: false },
        ...filtered,
      ];
    }

    return filtered;
  }, [query, normalizedOptions, withEmptyOption, emptyOptionLabel]);

  const [focusLocked, setFocusLocked] = React.useState(false);
  const [isMenuVisible, setIsMenuVisible] = React.useState(false);
  const [currentFocusIndex, setCurrentFocusIndex] = React.useState(-1);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = React.useState(false);
  const currentSelectionIndex = React.useMemo(() => {
    const allOptions = withEmptyOption
      ? [{ key: null, label: emptyOptionLabel ?? 'None' }, ...(options ?? [])]
      : options;
    return allOptions?.findIndex((it) => it.key === effectiveSelectedKey);
  }, [withEmptyOption, emptyOptionLabel, options, effectiveSelectedKey]);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const effectiveRootRef = ref ?? rootRef;
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const lastMousePositionRef = React.useRef({ x: 0, y: 0 });

  const getSelection = (key: string | number) => {
    const normalizedKey = key.toString().toLowerCase();
    return options?.find(
      (it) => it.key.toString().toLowerCase() === normalizedKey,
    );
  };

  const handleNewSelection = React.useCallback(
    (index = currentFocusIndex) => {
      const focusedOption = filteredOptions[index];

      onSelectionChange?.(focusedOption?.id as K);
      setTimeout(() => {
        setIsMenuVisible(false);
        setQuery('');
      });
    },
    [currentFocusIndex, filteredOptions, onSelectionChange],
  );

  const scrollToOption = React.useCallback(
    (index: number, block?: ScrollLogicalPosition) => {
      const optionElement = optionsContainerRef.current?.children[
        index
      ] as HTMLElement;

      if (optionElement) {
        requestAnimationFrame(() =>
          scrollIntoView({ element: optionElement, block }),
        );
      }
    },
    [],
  );

  const detectMouseMovement = React.useCallback((e: MouseEvent) => {
    const { x, y } = lastMousePositionRef.current;
    const threshold = 5; // pixels

    if (
      Math.abs(e.clientX - x) > threshold ||
      Math.abs(e.clientY - y) > threshold
    ) {
      setIsKeyboardNavigating(false);
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
    }
  }, []);

  const handleSearchKeydown = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      const focusable = triggerRef.current as HTMLDivElement;
      switch (e.key) {
        case 'ArrowDown': {
          e.stopPropagation();
          setIsKeyboardNavigating(true);
          if (isMenuVisible) {
            e.preventDefault();
            setCurrentFocusIndex((i) => {
              const newFocusIndex = Math.min(filteredOptions.length - 1, i + 1);

              scrollToOption(newFocusIndex);

              return newFocusIndex;
            });
          } else {
            setIsMenuVisible(true);
          }
          break;
        }

        case 'ArrowUp': {
          e.stopPropagation();
          setIsKeyboardNavigating(true);
          if (isMenuVisible) {
            e.preventDefault();
            setCurrentFocusIndex((i) => {
              const newFocusIndex = Math.max(0, i - 1);

              scrollToOption(newFocusIndex);

              return newFocusIndex;
            });
          } else {
            setIsMenuVisible(true);
          }
          break;
        }

        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsMenuVisible(false);
          setTimeout(() => focusable?.focus());
          break;

        case 'Enter':
          e.preventDefault();
          if (
            isMenuVisible &&
            !filteredOptions[currentFocusIndex]?.isDisabled
          ) {
            handleNewSelection();
          }
          break;

        case 'Tab':
          setIsMenuVisible(false);
          break;

        case 'Backspace':
        case 'ArrowLeft':
        case 'ArrowRight':
          break;

        default:
          if (!isMenuVisible) {
            setIsMenuVisible(true);
          }
          break;
      }

      onKeyDown?.(e);
    },
    [
      currentFocusIndex,
      filteredOptions,
      handleNewSelection,
      isMenuVisible,
      onKeyDown,
      scrollToOption,
      triggerRef,
    ],
  );

  const handleInputFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      searchInputRef.current?.select();
      onFocus?.(e);
    },
    [onFocus],
  );

  /**
   * Focus the search input when menu is visible and clear query when menu is hidden
   */
  React.useLayoutEffect(() => {
    if (isMenuVisible) {
      searchInputRef.current?.focus();
    } else {
      setQuery('');
    }
  }, [isMenuVisible]);

  /**
   * Set the current focus index to the selected item when menu becomes visible
   */
  React.useEffect(() => {
    if (isMenuVisible) {
      setCurrentFocusIndex(
        currentSelectionIndex != null && currentSelectionIndex >= 0
          ? currentSelectionIndex
          : 0,
      );
    }
  }, [isMenuVisible, currentSelectionIndex]);

  /**
   * Scroll the selected item into view when menu is visible
   */
  React.useLayoutEffect(() => {
    setTimeout(() => {
      if (
        optionsContainerRef.current &&
        currentSelectionIndex &&
        isMenuVisible
      ) {
        scrollToOption(currentSelectionIndex);
      }
    });
  }, [currentSelectionIndex, isMenuVisible, scrollToOption]);

  const hasCustomWidth = React.useMemo(() => {
    if (!props.className) return false;
    const classes = props.className.split(/\s+/);
    return classes.some((cls) =>
      /^(!?w-(?!full$|auto$|screen$)|!?min-w-(?!0$|full$)|!?max-w-)/.test(cls),
    );
  }, [props.className]);

  const { width: fieldWidth } = useResizeObserver({
    ref: effectiveRootRef as never,
  });

  /**
   * Set up mouse movement detection to re-enable mouse focusing after keyboard navigation
   */
  React.useEffect(() => {
    if (isMenuVisible) {
      document.addEventListener('mousemove', detectMouseMovement);
      return () => {
        document.removeEventListener('mousemove', detectMouseMovement);
      };
    }
  }, [isMenuVisible, detectMouseMovement]);

  /**
   * If autoSelectFirstOption is true and there are options, set the value to the first option
   */
  React.useEffect(() => {
    if (autoSelectFirstOption && options?.at(0)?.key && !props.value) {
      onSelectionChange?.(options?.[0]?.key);
    }
  }, [autoSelectFirstOption, onSelectionChange, options, props.value]);

  /**
   * Auto-focus the trigger when autoFocus is true
   */
  React.useEffect(() => {
    if (autoFocus && !isDisabled) {
      const focusable = searchInputRef.current;
      setTimeout(() => focusable?.focus());
    }
  }, [autoFocus, isDisabled]);

  return (
    <AriaSelect
      className="@Select"
      isInvalid={!!errorMessage}
      aria-label={label ?? 'Select'}
    >
      <div
        ref={effectiveRootRef}
        className={cn(
          '@Select relative flex w-full flex-col gap-2',
          fullHeight && 'h-full',
          props.className,
        )}
      >
        {label && <Label aria-required={isRequired}>{label}</Label>}
        <div
          ref={triggerRef}
          tabIndex={isDisabled ? undefined : 0}
          className={cn(
            fieldGroupVariants({
              disabled: isDisabled,
            }),
            '@Trigger flex w-full cursor-default justify-between',
            isDisabled && 'cursor-not-allowed',
            isInvalid && 'border-failure',
            selectionBoxClassName,
          )}
          onClick={() => {
            if (!focusLocked && !isMenuVisible) {
              setIsMenuVisible(true);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuVisible(true);
          }}
          onKeyDown={handleSearchKeydown}
        >
          <div
            ref={inputContainerRef}
            className={cn('relative flex w-full', hasCustomWidth && 'w-0 grow')}
          >
            {
              <div className={cn('line-clamp-1', query && 'opacity-0')}>
                {effectiveSelectedKey ? (
                  (getSelection(effectiveSelectedKey)?.selectedLabel ??
                  getSelection(effectiveSelectedKey)?.label ?? <>&nbsp;</>)
                ) : placeholder ? (
                  <span data-role="placeholder" className="text-neutral/40">
                    {placeholder}
                  </span>
                ) : (
                  <>&nbsp;</>
                )}
              </div>
            }
            <input
              ref={searchInputRef}
              className={cn(
                'absolute top-0 left-0 w-full cursor-default border-none bg-transparent p-0 text-sm text-neutral outline-none',
                isDisabled && 'cursor-not-allowed',
              )}
              style={{ width: inputContainerWidth ?? 0 }}
              type="text"
              value={query}
              onFocus={handleInputFocus}
              onChange={(e) => {
                setQuery(e.target.value);
                setCurrentFocusIndex(0);
              }}
              onBlur={() => {
                setIsMenuVisible(false);
              }}
              onKeyDown={handleSearchKeydown}
              disabled={isDisabled}
            />
          </div>
          {showArrow && (
            <div
              className={'[--color:theme(colors.neutral.400)] ml-2 flex gap-2'}
            >
              {props.isLoading ? (
                <LucideLoader
                  className={'size-[1.125em] animate-spin text-neutral/40'}
                />
              ) : (
                <LucideChevronDown
                  className={'size-[1.125em] text-neutral/40'}
                />
              )}
            </div>
          )}
        </div>
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
      </div>

      <Popover
        triggerRef={triggerRef}
        isOpen={!isDisabled && isMenuVisible}
        onOpenChange={(isOpen) => {
          setIsMenuVisible(isOpen);
          setFocusLocked(true);
          setTimeout(() => setFocusLocked(false), 500);
        }}
        placement="bottom left"
        className="no-scrollbar flex max-h-[40vh] overflow-auto"
        isNonModal
        offset={2}
      >
        <div
          tabIndex={-1}
          onFocus={() => setIsMenuVisible(true)}
          onBlur={() => setTimeout(() => setIsMenuVisible(false))}
          className={'box-border flex h-full max-h-[40vh] flex-col'}
          style={{ width: fieldWidth ? fieldWidth : 'auto' }}
        >
          {(query || !!filteredOptions.length) && (
            <div className="px-0.5 pt-px pb-0.5" ref={optionsContainerRef}>
              {filteredOptions?.map((it, index) => (
                <div
                  key={it?.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!it.isDisabled) {
                      handleNewSelection(index);
                    }
                  }}
                  onMouseEnter={() => {
                    if (!isKeyboardNavigating) {
                      setCurrentFocusIndex(index);
                    }
                  }}
                  className={cn(
                    'cursor-pointer',
                    it.isDisabled && 'cursor-not-allowed',
                  )}
                >
                  <div
                    className={cn(
                      'mt-px truncate rounded px-3 py-1 pr-6 text-sm',
                      currentSelectionIndex === index &&
                        'bg-primary/10 text-primary',
                      currentFocusIndex === index &&
                        !it.isDisabled &&
                        'bg-primary text-white',
                      it.isDisabled && 'text-neutral/60 opacity-50',
                    )}
                    data-type-id="re-ui-select-option"
                  >
                    {it?.label}
                  </div>
                </div>
              ))}
              {!filteredOptions.length && (
                <div className="mt-1 px-3 py-2.5 pr-6 text-sm text-neutral/60">
                  {t.components.select.noOptions}
                </div>
              )}
            </div>
          )}
        </div>
      </Popover>
    </AriaSelect>
  );
};
